import { Injectable, inject } from '@angular/core';
import { DialogPresenter } from '../medi-dialog/DialogPresenter';
import { DialogType } from '../medi-dialog/medi-dialog.component';
import { OroService } from '../oroservice';
import type { Case } from './models/case';
import type { CaseListRow } from './models/case-list-row';
import type { ExecuteLossCertificateWithdrawalRequest } from './models/execute-loss-certificate-withdrawal-request';
import { FutureLossCertificateManagement } from './models/future-loss-certificate-management';
import { LossCertificateSettingOutcome } from './models/loss-certificate-setting-outcome';
import type { LossCertificateWithdrawalPreparation } from './models/loss-certificate-withdrawal-preparation';
import type { LossCertificateWithdrawalResult } from './models/loss-certificate-withdrawal-result';
import type { SearchRequest } from './models/search-request';

@Injectable({
  providedIn: 'root',
})
export class CaseService {
  private readonly oroService = inject(OroService);
  private readonly dialog = inject(DialogPresenter);

  public async searchCases(search: SearchRequest): Promise<CaseListRow[]> {
    const cases = await this.oroService.post<CaseListRow[]>('web/dossier/search', search);
    return cases ?? [];
  }

  public async getCase(id: number | string): Promise<Case | null> {
    try {
      return await this.oroService.get<Case | null>(`web/dossierinfo?Id=${encodeURIComponent(String(id))}`);
    } catch {
      return null;
    }
  }

  public async sendReclamation(body: string, dossier: number): Promise<void> {
    await this.oroService.post<string>('web/sendreclamation', { body, dossier });
  }

  public async withdrawLossCertificates(cases: { Id: number }[]): Promise<boolean> {
    const caseIds = [...new Set<number>(cases.map((item) => item.Id))];
    let preparation = await this.prepare(caseIds);

    while (preparation) {
      const request = await this.confirm(preparation);

      if (!request) {
        return false;
      }

      const result = await this.oroService.post<LossCertificateWithdrawalResult>(
        'web/dossier/vswithdraw/execute',
        request,
      );

      if (result.RequiredConfirmation) {
        preparation = result.RequiredConfirmation;
        continue;
      }

      return await this.showResult(result);
    }

    return false;
  }

  private async prepare(caseIds: number[]): Promise<LossCertificateWithdrawalPreparation> {
    return await this.oroService.post<LossCertificateWithdrawalPreparation>('web/dossier/vswithdraw/prepare', {
      CaseIds: caseIds,
    });
  }

  private async confirm(
    preparation: LossCertificateWithdrawalPreparation,
  ): Promise<ExecuteLossCertificateWithdrawalRequest | undefined> {
    const finalPreparation = await this.reviewWarnings(preparation);

    if (!finalPreparation) {
      return undefined;
    }

    const confirmation = await this.confirmWithdrawal(finalPreparation.Count);

    if (confirmation !== true) {
      return undefined;
    }

    const futureManagement = await this.reviewManagementSettings(finalPreparation);

    if (futureManagement === undefined) {
      return undefined;
    }

    return {
      CaseIds: finalPreparation.CaseIds,
      Confirmation: finalPreparation.Confirmation,
      FutureManagement: futureManagement,
    };
  }

  private async reviewWarnings(
    initialPreparation: LossCertificateWithdrawalPreparation,
  ): Promise<LossCertificateWithdrawalPreparation | undefined> {
    let preparation = initialPreparation;
    const acceptedDunningCaseIds = new Set<number>();
    const acceptedEnforcementCaseIds = new Set<number>();

    while (preparation.CaseIds.length > 0) {
      const dunningCaseIds = preparation.DunningCaseIds.filter((id) => !acceptedDunningCaseIds.has(id));
      const dunningDecision = await this.reviewWarningGroup(
        dunningCaseIds,
        'LossCertificates.DunningWarningSingle',
        'LossCertificates.DunningWarning',
      );

      if (dunningDecision === undefined) {
        return undefined;
      }

      if (!dunningDecision && dunningCaseIds.length > 0) {
        const remainingCaseIds = preparation.CaseIds.filter((id) => !dunningCaseIds.includes(id));

        if (remainingCaseIds.length === 0) {
          return undefined;
        }

        preparation = await this.prepare(remainingCaseIds);
        continue;
      }

      dunningCaseIds.forEach((id) => acceptedDunningCaseIds.add(id));

      const enforcementCaseIds = preparation.EnforcementCaseIds.filter((id) => !acceptedEnforcementCaseIds.has(id));
      const enforcementDecision = await this.reviewWarningGroup(
        enforcementCaseIds,
        'LossCertificates.EnforcementWarningSingle',
        'LossCertificates.EnforcementWarning',
      );

      if (enforcementDecision === undefined) {
        return undefined;
      }

      if (!enforcementDecision && enforcementCaseIds.length > 0) {
        const remainingCaseIds = preparation.CaseIds.filter((id) => !enforcementCaseIds.includes(id));

        if (remainingCaseIds.length === 0) {
          return undefined;
        }

        preparation = await this.prepare(remainingCaseIds);
        continue;
      }

      enforcementCaseIds.forEach((id) => acceptedEnforcementCaseIds.add(id));

      return preparation;
    }

    return undefined;
  }

  private async reviewWarningGroup(
    warningCaseIds: number[],
    singularContent: string,
    pluralContent: string,
  ): Promise<boolean | undefined> {
    if (warningCaseIds.length === 0) {
      return true;
    }

    const content = warningCaseIds.length === 1 ? singularContent : pluralContent;
    const decision = await this.dialog.showDialog(
      'LossCertificates.WarningTitle',
      content,
      'LossCertificates.IncludeCases',
      'LossCertificates.ExcludeCases',
      DialogType.Warning,
      warningCaseIds.length.toString(),
    );

    return decision;
  }

  private async confirmWithdrawal(count: number): Promise<boolean | undefined> {
    const content = count === 1 ? 'LossCertificates.WithdrawalQuestion' : 'LossCertificates.BulkWithdrawalQuestion';
    const title = count === 1 ? 'LossCertificates.WithdrawalTitle' : 'LossCertificates.BulkWithdrawalTitle';

    return await this.dialog.showDialog(title, content, 'Yes', 'Cancel', DialogType.Question, count.toString());
  }

  private async reviewManagementSettings(
    preparation: LossCertificateWithdrawalPreparation,
  ): Promise<FutureLossCertificateManagement | undefined> {
    if (!preparation.AskFutureManagement) {
      return FutureLossCertificateManagement.Unchanged;
    }

    const decision = await this.dialog.showDialog(
      'LossCertificates.UpdateSettingsTitle',
      'LossCertificates.UpdateSettingsQuestion',
      'Yes',
      'No',
      DialogType.Question,
      '',
      'Cancel',
    );

    if (decision === undefined) {
      return undefined;
    }

    return decision ? FutureLossCertificateManagement.Keep : FutureLossCertificateManagement.Stop;
  }

  private async showResult(result: LossCertificateWithdrawalResult): Promise<boolean> {
    const affectedCount = result.CompletedIds.length + result.QueuedIds.length;

    if (result.Error) {
      await this.dialog.showDialog('LossCertificates.WithdrawalTitle', result.Error, 'Ok', '', DialogType.Error);
      return true;
    }

    if (affectedCount === 0) {
      return false;
    }

    const successMessage =
      affectedCount === 1
        ? 'LossCertificates.WithdrawalSuccessMessage'
        : 'LossCertificates.BulkWithdrawalSuccessMessage';
    await this.dialog.showDialog('Note', successMessage, 'Ok', '', DialogType.Info, affectedCount.toString());

    if (result.SettingOutcome === LossCertificateSettingOutcome.RemainingCases) {
      await this.dialog.showDialog('Note', 'LossCertificates.SettingRemainsEnabled', 'Ok', '', DialogType.Info);
    }

    return true;
  }
}
